const DB_NAME =
    'arento-analytics-v2';

const DB_VERSION =
    1;

const STORE_NAME =
    'events';

const QUEUED_AT_INDEX =
    'queuedAt';

let dbPromise = null;

function openDatabase() {
    if (dbPromise) {
        return dbPromise;
    }

    if (
        typeof window ===
        'undefined' ||
        !window.indexedDB
    ) {
        return Promise.reject(
            new Error(
                'Analytics V2: IndexedDB is unavailable'
            )
        );
    }

    dbPromise =
        new Promise(
            (
                resolve,
                reject
            ) => {
                const request =
                    window
                        .indexedDB
                        .open(
                            DB_NAME,
                            DB_VERSION
                        );

                request.onupgradeneeded =
                    event => {
                        const db =
                            event
                                .target
                                .result;

                        let store;

                        if (
                            !db
                                .objectStoreNames
                                .contains(
                                    STORE_NAME
                                )
                        ) {
                            store =
                                db
                                    .createObjectStore(
                                        STORE_NAME,
                                        {
                                            keyPath:
                                                'eventId'
                                        }
                                    );
                        } else {
                            store =
                                event
                                    .target
                                    .transaction
                                    .objectStore(
                                        STORE_NAME
                                    );
                        }

                        if (
                            !store
                                .indexNames
                                .contains(
                                    QUEUED_AT_INDEX
                                )
                        ) {
                            store
                                .createIndex(
                                    QUEUED_AT_INDEX,
                                    'queuedAt',
                                    {
                                        unique:
                                            false
                                    }
                                );
                        }
                    };

                request.onsuccess =
                    () => {
                        const db =
                            request.result;

                        db.onversionchange =
                            () => {
                                db.close();

                                dbPromise =
                                    null;
                            };

                        resolve(db);
                    };

                request.onerror =
                    () => {
                        dbPromise =
                            null;

                        reject(
                            request.error ||
                            new Error(
                                'Analytics V2: failed to open IndexedDB'
                            )
                        );
                    };

                request.onblocked =
                    () => {
                        console.warn(
                            '[AnalyticsV2] IndexedDB upgrade is blocked by another tab'
                        );
                    };
            }
        );

    return dbPromise;
}

function waitTransaction(
    transaction
) {
    return new Promise(
        (
            resolve,
            reject
        ) => {
            transaction.oncomplete =
                () => resolve();

            transaction.onerror =
                () =>
                    reject(
                        transaction.error ||
                        new Error(
                            'Analytics V2: IndexedDB transaction failed'
                        )
                    );

            transaction.onabort =
                () =>
                    reject(
                        transaction.error ||
                        new Error(
                            'Analytics V2: IndexedDB transaction aborted'
                        )
                    );
        }
    );
}

export async function enqueueAnalyticsEvent(
    rawEvent
) {
    if (!rawEvent?.eventId) {
        throw new Error(
            'Analytics V2: eventId is required for queue'
        );
    }

    const db =
        await openDatabase();

    const transaction =
        db.transaction(
            STORE_NAME,
            'readwrite'
        );

    transaction
        .objectStore(
            STORE_NAME
        )
        .put({
            eventId:
            rawEvent.eventId,

            queuedAt:
                Date.now(),

            rawEvent
        });

    await waitTransaction(
        transaction
    );

    return rawEvent.eventId;
}

export async function getOldestAnalyticsEvents(
    limit = 25
) {
    const safeLimit =
        Math.max(
            1,
            Math.min(
                25,
                Number(limit) ||
                25
            )
        );

    const db =
        await openDatabase();

    const transaction =
        db.transaction(
            STORE_NAME,
            'readonly'
        );

    const index =
        transaction
            .objectStore(
                STORE_NAME
            )
            .index(
                QUEUED_AT_INDEX
            );

    const rows = [];

    await new Promise(
        (
            resolve,
            reject
        ) => {
            const request =
                index.openCursor();

            request.onsuccess =
                event => {
                    const cursor =
                        event
                            .target
                            .result;

                    if (
                        !cursor ||
                        rows.length >=
                        safeLimit
                    ) {
                        resolve();
                        return;
                    }

                    rows.push(
                        cursor.value
                    );

                    cursor.continue();
                };

            request.onerror =
                () =>
                    reject(
                        request.error ||
                        new Error(
                            'Analytics V2: failed to read queue'
                        )
                    );
        }
    );

    await waitTransaction(
        transaction
    );

    return rows;
}

export async function deleteAnalyticsEvents(
    eventIds
) {
    const ids =
        Array.from(
            new Set(
                (eventIds || [])
                    .filter(Boolean)
            )
        );

    if (!ids.length) {
        return;
    }

    const db =
        await openDatabase();

    const transaction =
        db.transaction(
            STORE_NAME,
            'readwrite'
        );

    const store =
        transaction
            .objectStore(
                STORE_NAME
            );

    ids.forEach(
        eventId =>
            store.delete(
                eventId
            )
    );

    await waitTransaction(
        transaction
    );
}

export async function getAnalyticsQueueSize() {
    const db =
        await openDatabase();

    const transaction =
        db.transaction(
            STORE_NAME,
            'readonly'
        );

    const request =
        transaction
            .objectStore(
                STORE_NAME
            )
            .count();

    const count =
        await new Promise(
            (
                resolve,
                reject
            ) => {
                request.onsuccess =
                    () =>
                        resolve(
                            request.result
                        );

                request.onerror =
                    () =>
                        reject(
                            request.error
                        );
            }
        );

    await waitTransaction(
        transaction
    );

    return Number(
        count || 0
    );
}